import { useState } from 'react';
import ChainBadge from '../components/ChainBadge';
import { useNetwork } from '../contexts/NetworkContext';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useMyDIDs } from '../api/hooks';
import { apiFetch, ApiError } from '../api/client';
import { useWeb3 } from '../contexts/Web3Context';
import { useDIDRegistry } from '../hooks/useDIDRegistry';
import { txUrl, shortHash } from '../lib/chainExplorer';
import { describeTxError } from '../lib/txError';
import { generateEd25519KeyPair } from '@infinia/ida-sdk';
import { useAuth } from '../auth/AuthContext';

export default function MyDIDs() {
  const { data: dids, loading, error, refetch } = useMyDIDs();
  const [creating, setCreating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [keyType, setKeyType] = useState('Ed25519');
  const [copied, setCopied] = useState<string | null>(null);
  const [txResult, setTxResult] = useState<{ txHash: string; blockNumber: number } | null>(null);
  const [txError, setTxError] = useState<string | null>(null);
  const [canImport, setCanImport] = useState(false);
  const [savedKey, setSavedKey] = useState<{ did: string; hex: string; b64: string } | null>(null);
  const [keyCopied, setKeyCopied] = useState<'did' | 'hex' | 'b64' | null>(null);
  const [forceOffChain, setForceOffChain] = useState(false);
  // Issuers, verifiers and admins can create an organisation DID
  // (did:adi:org:...), the identifier for a merchant, bank or operator.
  const { hasRole } = useAuth();
  const canCreateOrg = hasRole('issuer', 'verifier', 'admin');
  const [orgDID, setOrgDID] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation(['dids', 'common']);

  const { account, isConnected, connect, isWrongNetwork, switchNetwork, provider } = useWeb3();
  const { selected, homeChainId } = useNetwork();
  // Decision 0: home chain mints bare DIDs; every other network chain-qualified.
  const didForAccount = (addr: string) => {
    const a = addr.toLowerCase();
    const cid = selected?.chainId ?? homeChainId;
    return cid === homeChainId ? `did:adi:${a}` : `did:adi:${cid}:${a}`;
  };
  const { createDID, pending: txPending, isReady: contractReady } = useDIDRegistry();

  const buildDIDDocument = (did: string, address: string) => ({
    '@context': ['https://www.w3.org/ns/did/v1'],
    id: did,
    verificationMethod: [
      {
        id: `${did}#key-1`,
        type: 'EcdsaSecp256k1VerificationKey2019',
        controller: did,
        blockchainAccountId: `eip155:${import.meta.env.VITE_BLOCKCHAIN_CHAIN_ID ?? 99999}:${address}`,
      },
    ],
    authentication: [`${did}#key-1`],
  });

  const handleCreateDID = async () => {
    setTxError(null);
    setTxResult(null);
    setCanImport(false);
    setCreating(true);

    try {
      // Guard: if the wallet is connected but on a different network than the
      // one selected in the header, STOP and ask — never silently create an
      // off-chain DID when the user expected an on-chain one.
      if (isConnected && isWrongNetwork && !forceOffChain) {
        setTxError(
          `Your wallet is on a different network than "${selected?.name ?? 'the selected network'}". ` +
          `Click "Switch" above (or pick the matching network in MetaMask), then try again — ` +
          `or tick "Create off-chain only" if that's what you want.`,
        );
        setCreating(false);
        return;
      }
      if (contractReady && account && !forceOffChain && !orgDID) {
        // --- On-chain path: MetaMask signs and submits the tx ---
        // Always generate an Ed25519 keypair so ZK Identity works with this DID.
        const ed25519kp = await generateEd25519KeyPair();
        // Web Crypto exports Ed25519 PKCS8: 48 bytes on Chrome (16-byte header + 32-byte seed).
        const rawPriv = ed25519kp.privateKey.length === 48 ? ed25519kp.privateKey.slice(16)
          : ed25519kp.privateKey.length === 72 ? ed25519kp.privateKey.slice(40)
          : ed25519kp.privateKey;
        const ed25519PubHex = Array.from(ed25519kp.publicKey).map(b => b.toString(16).padStart(2, '0')).join('');
        const ed25519PrivHex = Array.from(rawPriv).map(b => b.toString(16).padStart(2, '0')).join('');
        const ed25519PrivB64 = btoa(String.fromCharCode(...rawPriv));

        const did = didForAccount(account);
        const doc = buildDIDDocument(did, account);
        const result = await createDID(did, doc);
        setTxResult(result);

        // Sync off-chain state with the API after the tx is mined.
        // Include the Ed25519 public key so the backend embeds it as #key-2.
        await apiFetch<{ id: string }>('/dids', {
          method: 'POST',
          body: JSON.stringify({
            keyType,
            txHash: result.txHash,
            walletAddress: account,
            chainId: String(selected?.chainId ?? homeChainId),
            ed25519PublicKeyHex: ed25519PubHex,
          }),
        }).catch(() => {
          // Off-chain sync is best-effort; the on-chain tx is already final.
        });
        refetch();
        setShowCreateDialog(false);
        // Show the Ed25519 private key — needed for ZK Identity proof generation.
        setSavedKey({ did, hex: ed25519PrivHex, b64: ed25519PrivB64 });
      } else {
        // --- Off-chain fallback: API-only — backend generates keypair ---
        const resp = await apiFetch<{ id: string; privateKeyHex?: string; privateKeyBase64?: string }>('/dids', {
          method: 'POST',
          body: JSON.stringify(orgDID ? { keyType: 'Ed25519', entityType: 'organization' } : { keyType }),
        });
        refetch();
        setShowCreateDialog(false);
        // Show private key modal only for keypair-based DIDs (Ed25519 / Secp256k1)
        if (resp.privateKeyHex) {
          setSavedKey({ did: resp.id, hex: resp.privateKeyHex, b64: resp.privateKeyBase64 ?? '' });
        }
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes('already registered on-chain') || msg.includes('DID already exists')) {
        // DID exists on-chain but may not be in local DB yet — offer one-click import.
        setTxError('This wallet address already has a DID registered on-chain.');
        setCanImport(true);
      } else {
        setTxError(await describeTxError(e, { provider, account, chain: selected }));
      }
    } finally {
      setCreating(false);
    }
  };

  const handleImportDID = async () => {
    if (!account) return;
    setCreating(true);
    setTxError(null);
    try {
      await apiFetch('/dids', {
        method: 'POST',
        body: JSON.stringify({
          keyType,
          walletAddress: account,
          chainId: String(selected?.chainId ?? homeChainId),
        }),
      });
      refetch();
      setShowCreateDialog(false);
      setCanImport(false);
    } catch (e: unknown) {
      // A wallet's DID is derived from its address, so it is one DID per wallet
      // per network. When that DID cannot be imported, say why and how to get a
      // new one, instead of offering an Import that cannot succeed.
      const network = selected?.name ?? 'this network';
      if (e instanceof ApiError && e.code === 'DID_DEACTIVATED') {
        setCanImport(false);
        setTxError(
          `This wallet's DID on ${network} was deactivated. Deactivation is permanent, so it cannot be ` +
          `imported or registered again. To get a new DID on ${network}, switch MetaMask to a different ` +
          `account and create it from there.`,
        );
      } else if (e instanceof ApiError && e.code === 'DID_OWNED_ELSEWHERE') {
        setCanImport(false);
        setTxError(
          `This wallet's DID on ${network} is already linked to another IDA account, so it cannot be added ` +
          `here. Sign in with that account, or switch MetaMask to a different account to create a new DID.`,
        );
      } else {
        setTxError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setCreating(false);
    }
  };

  const copyDID = (did: string) => {
    navigator.clipboard.writeText(did);
    setCopied(did);
    setTimeout(() => setCopied(null), 2000);
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const list = Array.isArray(dids) ? dids : [];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('myDids.title')}</h1>
          <p className="mt-1 text-sm text-text-muted">{t('myDids.subtitle')}</p>
        </div>
        <button className="btn-primary" onClick={() => { setTxError(null); setTxResult(null); setCanImport(false); setForceOffChain(false); setShowCreateDialog(true); }}>
          <span className="flex items-center gap-2">
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add_circle</span>
            {t('myDids.createNew')}
          </span>
        </button>
      </div>

      {/* Private key save modal — shown once after keypair DID creation */}
      {savedKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-xl bg-amber-50">
                <span className="material-symbols-outlined text-amber-500" style={{ fontSize: 22 }}>key</span>
              </div>
              <div>
                <h2 className="text-base font-bold text-text-main">Save Your Private Key</h2>
                <p className="text-xs text-text-muted">This is shown only once and never stored. Copy it now.</p>
              </div>
            </div>

            <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              <span className="font-bold">Warning:</span> If you lose this key you cannot generate ZK proofs for credentials issued to this DID.
            </div>

            <div className="mb-1 flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-text-faint">DID</span>
              <button
                onClick={() => { navigator.clipboard.writeText(savedKey.did); setKeyCopied('did'); setTimeout(() => setKeyCopied(null), 2000); }}
                className="flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-bold text-primary hover:bg-blue-50"
              >
                <span className="material-symbols-outlined" style={{ fontSize: 12 }}>{keyCopied === 'did' ? 'check' : 'content_copy'}</span>
                {keyCopied === 'did' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <code className="mb-4 block break-all rounded-lg bg-slate-50 p-2 font-mono text-xs text-text-main">{savedKey.did}</code>

            <div className="mb-1 flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-text-faint">Private Key (hex)</span>
              <button
                onClick={() => { navigator.clipboard.writeText(savedKey.hex); setKeyCopied('hex'); setTimeout(() => setKeyCopied(null), 2000); }}
                className="flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-bold text-primary hover:bg-blue-50"
              >
                <span className="material-symbols-outlined" style={{ fontSize: 12 }}>{keyCopied === 'hex' ? 'check' : 'content_copy'}</span>
                {keyCopied === 'hex' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <code className="mb-4 block break-all rounded-lg bg-slate-50 p-2 font-mono text-xs text-text-main">{savedKey.hex}</code>

            <div className="mb-1 flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-text-faint">Private Key (base64)</span>
              <button
                onClick={() => { navigator.clipboard.writeText(savedKey.b64); setKeyCopied('b64'); setTimeout(() => setKeyCopied(null), 2000); }}
                className="flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-bold text-primary hover:bg-blue-50"
              >
                <span className="material-symbols-outlined" style={{ fontSize: 12 }}>{keyCopied === 'b64' ? 'check' : 'content_copy'}</span>
                {keyCopied === 'b64' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <code className="mb-5 block break-all rounded-lg bg-slate-50 p-2 font-mono text-xs text-text-main">{savedKey.b64}</code>

            <button
              className="btn-primary w-full"
              onClick={() => setSavedKey(null)}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>check_circle</span>
              I have saved my private key
            </button>
          </div>
        </div>
      )}

      {/* Last tx confirmation banner */}
      {txResult && (
        <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-emerald-700">
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>check_circle</span>
            <span>DID registered on-chain — block {txResult.blockNumber}</span>
          </div>
          {txUrl(txResult.txHash) ? (
            <a href={txUrl(txResult.txHash)!} target="_blank" rel="noopener noreferrer"
              className="text-xs font-bold text-emerald-700 underline">
              {shortHash(txResult.txHash)} ↗
            </a>
          ) : (
            <span className="font-mono text-xs text-emerald-600">{shortHash(txResult.txHash)}</span>
          )}
          <button onClick={() => setTxResult(null)} className="ml-2 text-emerald-500 hover:text-emerald-700">
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
          </button>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-xl border border-border-subtle bg-white p-4">
          <p className="text-xs font-bold text-text-faint">{t('myDids.stats.total')}</p>
          <p className="mt-1 text-2xl font-bold text-text-main">{list.length}</p>
        </div>
        <div className="rounded-xl border border-border-subtle bg-white p-4">
          <p className="text-xs font-bold text-text-faint">{t('myDids.stats.active')}</p>
          <p className="mt-1 text-2xl font-bold text-emerald-600">{list.filter(d => d.status === 'active').length}</p>
        </div>
        <div className="rounded-xl border border-border-subtle bg-white p-4">
          <p className="text-xs font-bold text-text-faint">{t('myDids.stats.keyTypes')}</p>
          <div className="mt-1 flex gap-2">
            {['Ed25519', 'Secp256k1'].map(kt => {
              const count = list.filter(d => d.keyType.includes(kt.replace('Secp256k1', 'Ecdsa'))).length || list.filter(d => d.keyType === kt).length;
              return count > 0 ? (
                <span key={kt} className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-bold text-primary">{kt}: {count}</span>
              ) : null;
            })}
          </div>
        </div>
      </div>

      {showCreateDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-text-main">{t('myDids.createDialog.title')}</h2>
              <button onClick={() => { setShowCreateDialog(false); setCanImport(false); setTxError(null); setForceOffChain(false); }} className="rounded-lg p-1 text-text-faint hover:bg-slate-100">
                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
              </button>
            </div>

            <div className="space-y-4">
              {canCreateOrg && (
                <label className="flex items-start gap-2 rounded-lg border border-border-subtle p-3 text-xs text-text-main">
                  <input id="create-org-did" type="checkbox" checked={orgDID} onChange={(e) => setOrgDID(e.target.checked)} className="mt-0.5" />
                  <span>
                    <span className="font-bold">Organisation DID</span> (did:adi:org:…) for a business such as a merchant,
                    bank or agent operator. It is key-based: save the private key when it is shown.
                  </span>
                </label>
              )}
              {/* MetaMask status strip */}
              {!isConnected ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold text-amber-800">Wallet not connected</p>
                    <p className="text-[11px] text-amber-700 mt-0.5">Connect MetaMask to register on-chain, or create off-chain only.</p>
                  </div>
                  <button onClick={connect}
                    className="ml-3 shrink-0 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700">
                    Connect
                  </button>
                </div>
              ) : isWrongNetwork ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-center justify-between">
                  <p className="text-xs font-bold text-red-700">Wallet is on the wrong network — switch to {selected?.name ?? 'the selected network'} to register on-chain.</p>
                  <button onClick={switchNetwork}
                    className="ml-3 shrink-0 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700">
                    Switch
                  </button>
                </div>
              ) : (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <div className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-emerald-500 shrink-0" />
                    <p className="text-xs text-emerald-700">
                      <span className="font-bold">MetaMask connected</span> — DID will be registered on {selected?.name ?? 'chain'} as{' '}
                      <code className="font-mono">{account ? `${didForAccount(account).slice(0, 26)}…` : ''}</code>
                    </p>
                  </div>
                  <p className="mt-1.5 text-[11px] text-emerald-600">
                    An Ed25519 keypair will also be generated for ZK Identity — save it when shown.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-bold text-text-main mb-2">{t('myDids.createDialog.keyTypeLabel')}</label>
                <div className="grid grid-cols-2 gap-3">
                  {['Ed25519', 'Secp256k1'].map(kt => (
                    <button
                      key={kt}
                      onClick={() => setKeyType(kt)}
                      className={`rounded-xl border-2 p-4 text-left transition-all ${
                        keyType === kt ? 'border-primary bg-blue-50' : 'border-border-subtle hover:border-primary/20'
                      }`}
                    >
                      <p className="text-sm font-bold text-text-main">{kt}</p>
                      <p className="mt-0.5 text-xs text-text-faint">
                        {kt === 'Ed25519' ? t('myDids.createDialog.ed25519Desc') : t('myDids.createDialog.secp256k1Desc')}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-slate-50 p-3">
                <p className="text-xs text-text-faint">
                  <Trans
                    i18nKey="dids:myDids.createDialog.format"
                    values={{ keyType }}
                    components={{ 1: <strong />, code: <code /> }}
                  />
                </p>
              </div>

              {txError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                  <p className="text-xs text-red-600">{txError}</p>
                  {canImport && (
                    <p className="mt-1 text-xs text-red-500">
                      Import it below to add it to your DID list without re-registering on-chain.
                    </p>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button onClick={() => setShowCreateDialog(false)}
                  className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-bold text-text-main hover:bg-slate-50">
                  {t('common:actions.cancel')}
                </button>
                {canImport ? (
                  <button onClick={handleImportDID} disabled={creating}
                    className="btn-primary">
                    {creating ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Importing…
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>download</span>
                        Import to My DIDs
                      </span>
                    )}
                  </button>
                ) : (
                  <button onClick={handleCreateDID} disabled={creating || txPending}
                    className="btn-primary">
                    {(creating || txPending) ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        {contractReady ? 'Waiting for MetaMask…' : t('myDids.createDialog.creating')}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        {contractReady && (
                          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>account_balance_wallet</span>
                        )}
                        {contractReady ? 'Sign & Register On-Chain' : t('myDids.createDialog.create')}
                      </span>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="text-center py-20 text-red-500">{t('common:errors.loadFailed')}</div>
      ) : list.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border-subtle py-20">
          <span className="material-symbols-outlined mb-4 text-text-faint" style={{ fontSize: 48 }}>badge</span>
          <p className="text-sm font-bold text-text-main">{t('myDids.empty.title')}</p>
          <p className="mt-1 text-xs text-text-faint">{t('myDids.empty.subtitle')}</p>
          <button onClick={() => setShowCreateDialog(true)} className="btn-primary mt-4">
            <span className="material-symbols-outlined mr-1" style={{ fontSize: 16 }}>add_circle</span>
            {t('myDids.empty.create')}
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {list.map((d) => (
            <div
              key={d.did}
              onClick={() => navigate(`/my-dids/${encodeURIComponent(d.did)}`)}
              className="group cursor-pointer rounded-xl border border-border-subtle bg-white p-5 shadow-sm transition-all hover:shadow-md hover:border-primary/20"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="flex size-12 items-center justify-center rounded-xl bg-blue-50 group-hover:bg-primary/10 transition-colors">
                    <span className="material-symbols-outlined text-primary" style={{ fontSize: 24 }}>badge</span>
                  </div>
                  <div>
                    <p className="flex items-center gap-2 text-sm font-bold text-text-main">{d.label} <ChainBadge did={d.did} /></p>
                    <div className="mt-1.5 flex items-center gap-2">
                      <code className="inline-block max-w-xs truncate rounded border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-xs text-text-main" title={d.did}>
                        {d.did}
                      </code>
                      <button
                        onClick={(e) => { e.stopPropagation(); copyDID(d.did); }}
                        className="rounded p-0.5 text-text-faint transition-colors hover:text-primary hover:bg-blue-50"
                        title={t('myDids.card.copyHint')}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
                          {copied === d.did ? 'check' : 'content_copy'}
                        </span>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/resolve?did=${encodeURIComponent(d.did)}`); }}
                        className="rounded p-0.5 text-text-faint transition-colors hover:text-primary hover:bg-blue-50"
                        title="Resolve DID"
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>travel_explore</span>
                      </button>
                      {copied === d.did && (
                        <span className="text-[10px] font-bold text-emerald-600">{t('myDids.card.copied')}</span>
                      )}
                    </div>
                    <div className="mt-2.5 flex items-center gap-4 text-xs text-text-faint">
                      <span className="flex items-center gap-1">
                        <span className="material-symbols-outlined" style={{ fontSize: 12 }}>key</span>
                        {d.keyType}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="material-symbols-outlined" style={{ fontSize: 12 }}>schedule</span>
                        {formatDate(d.created)}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="material-symbols-outlined" style={{ fontSize: 12 }}>verified</span>
                        {t('myDids.card.credentials', { count: d.credentials })}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                    d.status === 'active'
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-red-200 bg-red-50 text-red-700'
                  }`}>
                    <span className={`size-1.5 rounded-full ${d.status === 'active' ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                    {d.status}
                  </span>
                  <span className="material-symbols-outlined text-text-faint opacity-0 group-hover:opacity-100 transition-opacity" style={{ fontSize: 18 }}>
                    chevron_right
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
